import * as React from "react";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SchemaIcon from "@mui/icons-material/Schema";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import AdminPageHeader from "../components/AdminPageHeader";
import { SUBSCRIPTION_SOURCES } from "../constants/admin";
import { useApi } from "../hooks/useApi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export default function AdminSubscriptions() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const squarespaceCustomerIdFromUrl = searchParams.get("squarespace_customer_id")?.trim() || "";
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("");
  const [sourceFilter, setSourceFilter] = React.useState("");
  const [pagination] = React.useState({
    total: 0,
    limit: 20,
    offset: 0,
    page: 1,
  });
  const [schemaDialogOpen, setSchemaDialogOpen] = React.useState(false);
  const [schemaTableName, setSchemaTableName] = React.useState("");
  const [schemaData, setSchemaData] = React.useState(null);
  const [loadingSchema, setLoadingSchema] = React.useState(false);
  const [schemaError, setSchemaError] = React.useState(null);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "subscriptions",
      debouncedSearch,
      statusFilter,
      sourceFilter,
      squarespaceCustomerIdFromUrl,
      pagination.offset,
      pagination.limit,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch?.trim()) params.append("search", debouncedSearch.trim());
      if (statusFilter) params.append("status", statusFilter);
      if (sourceFilter?.trim()) params.append("source", sourceFilter.trim());
      if (squarespaceCustomerIdFromUrl) {
        params.append("squarespace_customer_id", squarespaceCustomerIdFromUrl);
      }
      params.append("limit", pagination.limit.toString());
      params.append("offset", pagination.offset.toString());
      return api.get(`/admin/subscriptions?${params.toString()}`);
    },
  });

  const subscriptions = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;
  const loading = isLoading;

  const handleViewSubscription = (subscriptionId) => {
    if (subscriptionId) {
      navigate(`/dashboard/subscriptions/${subscriptionId}`);
    }
  };

  const handleViewOrganization = (organizationId) => {
    if (organizationId) {
      navigate(`/dashboard/organizations/${organizationId}`);
    }
  };

  const handleCloseSchemaDialog = () => {
    setSchemaDialogOpen(false);
    setSchemaTableName("");
    setSchemaData(null);
    setSchemaError(null);
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
          <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Admin
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Subscriptions
                </Typography>
              </Breadcrumbs>
            </Box>

            <AdminPageHeader
              title="Subscriptions"
              description="Manage all subscriptions in the system"
              icon={<SubscriptionsIcon sx={{ color: "primary.main" }} />}
            >
              <Button
                variant="contained"
                sx={{ bgcolor: "white", color: "black", "&:hover": { bgcolor: "grey.100" } }}
                startIcon={<SchemaIcon />}
                onClick={async () => {
                  setSchemaDialogOpen(true);
                  setSchemaTableName("subscriptions");
                  setSchemaData(null);
                  setSchemaError(null);
                  setLoadingSchema(true);
                  setSchemaError(null);
                  try {
                    const data = await api.get("/admin/schema/subscriptions");
                    setSchemaData(data);
                  } catch (err) {
                    console.error("Error fetching schema:", err);
                    setSchemaError(err.message || "Failed to fetch schema");
                  } finally {
                    setLoadingSchema(false);
                  }
                }}
              >
                Schema
              </Button>
            </AdminPageHeader>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error.message || "Failed to fetch subscriptions"}
              </Alert>
            )}

            <Card>
              <CardContent>
                <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Search subscriptions by name, type, or organization..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                      <MenuItem value="expired">Expired</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Source</InputLabel>
                    <Select
                      value={sourceFilter}
                      onChange={(e) => setSourceFilter(e.target.value)}
                      label="Source"
                    >
                      <MenuItem value="">All</MenuItem>
                      {SUBSCRIPTION_SOURCES.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {squarespaceCustomerIdFromUrl ? (
                  <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={
                      <IconButton
                        size="small"
                        aria-label="Clear Squarespace customer filter"
                        onClick={() => {
                          const next = new URLSearchParams(searchParams);
                          next.delete("squarespace_customer_id");
                          setSearchParams(next);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    Filtered by <code style={{ fontFamily: "monospace" }}>meta_data.squarespace_customer_id</code> ={" "}
                    <strong style={{ fontFamily: "monospace" }}>{squarespaceCustomerIdFromUrl}</strong>
                  </Alert>
                ) : null}

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Plan</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Organization</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>Renewal Date</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        Array.from({ length: pagination.limit }).map((_, index) => (
                          <TableRow key={`skeleton-${index}`}>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={20} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={24} width={80} borderRadius={4} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={20} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={20} width={80} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={20} width={100} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Skeleton height={20} width={100} />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }} align="right">
                              <Skeleton height={32} width={80} />
                            </TableCell>
                          </TableRow>
                        ))
                      ) : subscriptions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 1.5 }}>
                            <Typography color="text.secondary">
                              {searchQuery || statusFilter || sourceFilter ? "No subscriptions found matching your filters" : "No subscriptions found"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        subscriptions.map((subscription) => (
                          <TableRow key={subscription.id}>
                            <TableCell sx={{ py: 1.5 }}>
                              {subscription.plan_legacy || subscription.plan || "N/A"}
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Chip
                                label={subscription.status || "Unknown"}
                                color={
                                  subscription.status === "active"
                                    ? "success"
                                    : subscription.status === "cancelled"
                                    ? "error"
                                    : subscription.status === "expired"
                                    ? "warning"
                                    : "default"
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              {subscription.dba_name || subscription.organization_name || "N/A"}
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              {subscription.amount
                                ? `$${parseFloat(subscription.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : "N/A"}
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              {subscription.start_date
                                ? new Date(subscription.start_date).toLocaleDateString()
                                : "N/A"}
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              {subscription.renewal_date
                                ? new Date(subscription.renewal_date).toLocaleDateString()
                                : "N/A"}
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }} align="right">
                              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => handleViewSubscription(subscription.id)}
                                >
                                  View
                                </Button>
                                {subscription.organization_id && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleViewOrganization(subscription.organization_id)}
                                  >
                                    Org
                                  </Button>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {!loading && total > pagination.limit && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {pagination.offset + 1}-
                      {Math.min(pagination.offset + pagination.limit, total)} of {total} subscriptions
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Container>
          <Footer />
        </Box>
      </Box>

      {/* Schema Dialog */}
      <Dialog
        open={schemaDialogOpen}
        onClose={handleCloseSchemaDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Table Schema</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Table Name</InputLabel>
              <Select
                value={schemaTableName}
                onChange={(e) => {
                  setSchemaTableName(e.target.value);
                  // Automatically fetch schema when selection changes
                  if (e.target.value) {
                    const fetchSchema = async () => {
                      setLoadingSchema(true);
                      setSchemaError(null);
                      setSchemaData(null);
                      try {
                        const data = await api.get(`/admin/schema/${e.target.value}`);
                        setSchemaData(data);
                      } catch (err) {
                        console.error("Error fetching schema:", err);
                        setSchemaError(err.message || "Failed to fetch schema");
                      } finally {
                        setLoadingSchema(false);
                      }
                    };
                    fetchSchema();
                  }
                }}
                label="Table Name"
              >
                <MenuItem value="organizations">organizations</MenuItem>
                <MenuItem value="users">users</MenuItem>
                <MenuItem value="subscriptions">subscriptions</MenuItem>
                <MenuItem value="orders">orders</MenuItem>
                <MenuItem value="products">products</MenuItem>
                <MenuItem value="contacts">contacts</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {schemaError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {schemaError}
            </Alert>
          )}

          {loadingSchema && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {schemaData && (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Table: {schemaData.table_name}
              </Typography>
              
              {/* Columns */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                Columns
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Nullable</TableCell>
                      <TableCell>Default</TableCell>
                      <TableCell>Position</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schemaData.columns.map((col) => (
                      <TableRow key={col.name}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={schemaData.primary_keys?.includes(col.name) ? "bold" : "normal"}>
                            {col.name}
                            {schemaData.primary_keys?.includes(col.name) && " (PK)"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {col.type}
                            {col.max_length && `(${col.max_length})`}
                            {col.precision && col.scale && `(${col.precision},${col.scale})`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={col.nullable ? "YES" : "NO"}
                            size="small"
                            color={col.nullable ? "default" : "warning"}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                            {col.default || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>{col.position}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Foreign Keys */}
              {schemaData.foreign_keys && schemaData.foreign_keys.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Foreign Keys
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Column</TableCell>
                          <TableCell>References Table</TableCell>
                          <TableCell>References Column</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schemaData.foreign_keys.map((fk, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{fk.column}</TableCell>
                            <TableCell>{fk.references_table}</TableCell>
                            <TableCell>{fk.references_column}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Unique Constraints */}
              {schemaData.unique_constraints && schemaData.unique_constraints.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Unique Constraints
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {schemaData.unique_constraints.map((uc, idx) => (
                      <Chip
                        key={idx}
                        label={`${uc.column} (${uc.constraint_name})`}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                </>
              )}

              {/* Indexes */}
              {schemaData.indexes && schemaData.indexes.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Indexes
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Definition</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schemaData.indexes.map((idx) => (
                          <TableRow key={idx.name}>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace">
                                {idx.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                                {idx.definition}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Raw JSON View */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Raw JSON
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      overflow: "auto",
                      maxHeight: "300px",
                      m: 0,
                    }}
                  >
                    {JSON.stringify(schemaData, null, 2)}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSchemaDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
